import React from 'react';
import { HStack } from '@chakra-ui/react';
import type { StackProps } from '@chakra-ui/react';

// =============================================================================
// OMNI AppHeader Container Components
// =============================================================================
// Universal container components using OMNI AppHeader recipes for consistent
// layout and styling across all header sections with proper responsive design
// =============================================================================

// OMNI AppHeader Main Container
// =============================================================================
// Main header container with OMNI styling and responsive design
export interface OmniAppHeaderContainerProps extends StackProps {
  elevated?: boolean;
  compact?: boolean;
}

export const OmniAppHeaderContainer = React.forwardRef<
  HTMLDivElement,
  OmniAppHeaderContainerProps
>((props, ref) => {
  const { elevated = false, compact = false, ...rest } = props;

  return (
    <HStack
      ref={ref}
      as="header"
      css={{
        // Apply OMNI header container recipe
        '&[data-recipe="omniAppHeaderContainer"]':
          'var(--recipes-omniAppHeaderContainer)',
      }}
      data-recipe="omniAppHeaderContainer"
      data-elevated={elevated}
      data-compact={compact}
      {...rest}
    />
  );
});

OmniAppHeaderContainer.displayName = 'OmniAppHeaderContainer';

// OMNI AppHeader Section
// =============================================================================
// Left and right section containers within the header
export interface OmniAppHeaderSectionProps extends StackProps {
  position?: 'left' | 'right' | 'center';
  spacing?: 'tight' | 'normal' | 'loose';
}

export const OmniAppHeaderSection = React.forwardRef<
  HTMLDivElement,
  OmniAppHeaderSectionProps
>((props, ref) => {
  const { position = 'left', spacing = 'normal', ...rest } = props;

  return (
    <HStack
      ref={ref}
      css={{
        // Apply OMNI header section recipe
        '&[data-recipe="omniAppHeaderSection"]':
          'var(--recipes-omniAppHeaderSection)',
      }}
      data-recipe="omniAppHeaderSection"
      data-position={position}
      data-spacing={spacing}
      {...rest}
    />
  );
});

OmniAppHeaderSection.displayName = 'OmniAppHeaderSection';

// OMNI Brand Container
// =============================================================================
// Container for logo and brand title with OMNI spacing
export interface OmniBrandContainerProps extends StackProps {
  size?: 'sm' | 'md' | 'lg';
}

export const OmniBrandContainer = React.forwardRef<
  HTMLDivElement,
  OmniBrandContainerProps
>((props, ref) => {
  const { size = 'md', ...rest } = props;

  return (
    <HStack
      ref={ref}
      css={{
        // Apply OMNI brand container recipe
        '&[data-recipe="omniBrandContainer"]':
          'var(--recipes-omniBrandContainer)',
      }}
      data-recipe="omniBrandContainer"
      data-size={size}
      {...rest}
    />
  );
});

OmniBrandContainer.displayName = 'OmniBrandContainer';

// OMNI Navigation Container
// =============================================================================
// Container for navigation elements like CheatSheet and History
export interface OmniNavigationContainerProps extends StackProps {
  spacing?: 'tight' | 'normal' | 'loose';
}

export const OmniNavigationContainer = React.forwardRef<
  HTMLDivElement,
  OmniNavigationContainerProps
>((props, ref) => {
  const { spacing = 'normal', ...rest } = props;

  return (
    <HStack
      ref={ref}
      css={{
        // Apply OMNI navigation container recipe
        '&[data-recipe="omniNavigationContainer"]':
          'var(--recipes-omniNavigationContainer)',
      }}
      data-recipe="omniNavigationContainer"
      data-spacing={spacing}
      {...rest}
    />
  );
});

OmniNavigationContainer.displayName = 'OmniNavigationContainer';

// =============================================================================
// EXPORTS
// =============================================================================
// All component types are already exported with their declarations above
